import React, { useEffect, useState } from 'react';
import {
  TrendingUp,

  Users,
  Calendar,
  MapPin,
  DollarSign,
  BarChart3,
  PieChart,
  Activity,
  Download,
  RefreshCw
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ApiService } from '../services/api';
import { RouteService } from '../services/routeService';
import { BusService } from '../services/busService';
import { DashboardStats, RevenueData } from '../types';
import toast from 'react-hot-toast';

// Mock chart components (you can replace with real chart libraries)
const LineChart = ({ title }: { title: string }) => (
  <div className="h-64 flex items-center justify-center bg-secondary-50 rounded-lg">
    <div className="text-center">
      <BarChart3 className="h-12 w-12 text-secondary-400 mx-auto mb-2" />
      <p className="text-secondary-500">{title}</p>
      <p className="text-sm text-secondary-400">Chart visualization</p>
    </div>
  </div>
);

const PieChartComponent = ({ title }: { title: string }) => (
  <div className="h-64 flex items-center justify-center bg-secondary-50 rounded-lg">
    <div className="text-center">
      <PieChart className="h-12 w-12 text-secondary-400 mx-auto mb-2" />
      <p className="text-secondary-500">{title}</p>
      <p className="text-sm text-secondary-400">Chart visualization</p>
    </div>
  </div>
);

export const Analytics: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [routeAnalytics, setRouteAnalytics] = useState<any[]>([]);
  const [busAnalytics, setBusAnalytics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');
  // const [selectedMetric, setSelectedMetric] = useState('revenue');

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      const [statsResult, routes, buses] = await Promise.all([
        ApiService.getDashboardStats(),
        RouteService.getRoutes(),
        BusService.getBuses()
      ]);

      if (statsResult.success && statsResult.data) {
        setStats(statsResult.data);

        // Calculate route analytics
        const routeStats = routes.map(route => ({
          route: `${route.fromCity} → ${route.toCity}`,
          totalRevenue: 0, // Requires booking aggregation
          utilizationRate: 0, // Seat data removed from route
          averagePrice: route.price,
          totalBookings: 0 // Requires booking aggregation
        }));
        setRouteAnalytics(routeStats);

        // Calculate bus analytics
        const busStats = buses.map(bus => ({
          busNumber: bus.busNumber,
          operatorName: bus.operatorName,
          totalSeats: bus.totalSeats,
          availableSeats: bus.availableSeats,
          utilizationRate: ((bus.totalSeats - bus.availableSeats) / bus.totalSeats) * 100,
          isActive: bus.isActive,
          isInService: bus.isInService
        }));
        setBusAnalytics(busStats);

        // Load real analytics data
        await loadRealAnalyticsData();
      } else {
        toast.error(statsResult.error || 'Failed to load analytics data');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadRealAnalyticsData = async () => {
    try {
      const revenueResult = await ApiService.getRevenueData(dateRange);
      if (revenueResult.success && revenueResult.data) {
        setRevenueData(revenueResult.data);
      } else {
        // Fallback to empty data if real data fails
        setRevenueData([]);
      }
    } catch (error) {
      console.error('Error loading real analytics data:', error);
      setRevenueData([]);
    }
  };

  const exportAnalytics = () => {
    const csvContent = [
      ['Date', 'Revenue', 'Bookings'],
      ...revenueData.map(item => [
        item.date,
        item.revenue.toString(),
        item.bookings.toString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success('Analytics data exported successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary-500">Failed to load analytics data</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Analytics</h1>
          <p className="text-secondary-600">Comprehensive insights and performance metrics</p>
        </div>
        <div className="flex space-x-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            onClick={loadAnalyticsData}
          >
            Refresh
          </Button>
          <Button
            variant="secondary"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={exportAnalytics}
          >
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Revenue</p>
                <p className="text-2xl font-bold text-secondary-900">
                  ₵{stats.totalRevenue.toLocaleString()}
                </p>
                <div className="flex items-center mt-1">
                  <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+{stats.revenueGrowth}%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Users</p>
                <p className="text-2xl font-bold text-secondary-900">
                  {stats.totalUsers.toLocaleString()}
                </p>
                <div className="flex items-center mt-1">
                  <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+{stats.userGrowth}%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Bookings</p>
                <p className="text-2xl font-bold text-secondary-900">
                  {stats.totalBookings.toLocaleString()}
                </p>
                <div className="flex items-center mt-1">
                  <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+{stats.bookingGrowth}%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <MapPin className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Active Routes</p>
                <p className="text-2xl font-bold text-secondary-900">
                  {stats.totalRoutes.toLocaleString()}
                </p>
                <div className="flex items-center mt-1">
                  <Activity className="h-4 w-4 text-blue-500 mr-1" />
                  <span className="text-sm text-blue-600">Active</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <LineChart title="Revenue Over Time" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Booking Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <PieChartComponent
              title="Booking Status"
            />
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Top Performing Routes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {routeAnalytics.slice(0, 5).map((route, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{route.route}</p>
                    <p className="text-sm text-secondary-500">{route.totalBookings} bookings</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">₵{route.totalRevenue.toFixed(0)}</p>
                    <p className="text-sm text-green-600">{route.utilizationRate.toFixed(1)}% utilization</p>
                  </div>
                </div>
              ))}
              {routeAnalytics.length === 0 && (
                <p className="text-secondary-500 text-center py-4">No route data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Bus Fleet Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {busAnalytics.slice(0, 5).map((bus, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{bus.busNumber}</p>
                    <p className="text-sm text-secondary-500">{bus.operatorName}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{bus.utilizationRate.toFixed(1)}% utilized</p>
                    <p className="text-sm text-secondary-500">{bus.availableSeats}/{bus.totalSeats} seats</p>
                  </div>
                </div>
              ))}
              {busAnalytics.length === 0 && (
                <p className="text-secondary-500 text-center py-4">No bus data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Conversion Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Booking Conversion</span>
                <span className="font-bold">12.5%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Payment Success</span>
                <span className="font-bold">94.2%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">User Retention</span>
                <span className="font-bold">78.3%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Customer Satisfaction</span>
                <span className="font-bold">4.6/5</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-secondary-100">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                <div>
                  <p className="text-sm font-medium">New booking completed</p>
                  <p className="text-xs text-secondary-500">Accra → Kumasi • 2 minutes ago</p>
                </div>
              </div>
              <span className="text-sm font-bold text-green-600">₵45.00</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-secondary-100">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                <div>
                  <p className="text-sm font-medium">New user registered</p>
                  <p className="text-xs text-secondary-500">John Doe • 5 minutes ago</p>
                </div>
              </div>
              <span className="text-sm text-secondary-500">User</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-secondary-100">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                <div>
                  <p className="text-sm font-medium">Route updated</p>
                  <p className="text-xs text-secondary-500">Accra → Tamale • 10 minutes ago</p>
                </div>
              </div>
              <span className="text-sm text-secondary-500">Admin</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                <div>
                  <p className="text-sm font-medium">Payment processed</p>
                  <p className="text-xs text-secondary-500">Mobile Money • 15 minutes ago</p>
                </div>
              </div>
              <span className="text-sm font-bold text-green-600">₵80.00</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
